import React, { useState } from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { Plus } from 'lucide-react';

interface CreateOrgModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateOrgModal: React.FC<CreateOrgModalProps> = ({ isOpen, onClose }) => {
  const { createOrganization } = useAuth();
  const { showToast } = useToast();

  const [newOrgName, setNewOrgName] = useState('');
  const [isCreatingOrg, setIsCreatingOrg] = useState(false);

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newOrgName.trim()) return;

    setIsCreatingOrg(true);
    try {
      const res = await createOrganization(newOrgName.trim());
      showToast({
        type: 'success',
        title: 'Organization Created',
        message: `Organization "${res.organization.name}" and default project created successfully.`,
      });
      setNewOrgName('');
      onClose();
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Failed to Create Organization',
        message: err?.message || 'Server error occurred.',
      });
    } finally {
      setIsCreatingOrg(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create New Organization"
    >
      <form onSubmit={handleCreateOrg} className="space-y-4">
        <p className="text-xs text-cf-textSecondary">
          Organizations group your projects and team members together.
        </p>

        <Input
          label="Organization Name"
          size="xs"
          required
          value={newOrgName}
          onChange={(e) => setNewOrgName(e.target.value)}
          placeholder="e.g. Acme Corporation"
        />

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="secondary"
            size="xs"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="xs"
            disabled={isCreatingOrg || !newOrgName.trim()}
            isLoading={isCreatingOrg}
            leftIcon={<Plus size={13} />}
          >
            Create Organization
          </Button>
        </div>
      </form>
    </Modal>
  );
};
